import React from 'react';
import { motion } from 'framer-motion';
import {
  PenTool, Shirt, Home, Wrench,
  Coins, Star, CheckCircle, Users, BookOpen, HardHat, Play
} from 'lucide-react';

// Pixel Art SVG Components for each quest
const PixelHandwriting = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    {/* Paper background */}
    <rect x="8" y="4" width="48" height="56" fill="#F5F5DC" />
    <rect x="8" y="4" width="48" height="4" fill="#E8E8D0" />
    {/* Lines on paper */}
    {[16, 24, 32, 40, 48].map((y) => (
      <rect key={y} x="12" y={y} width="40" height="1" fill="#87CEEB" opacity="0.5" />
    ))}
    {/* Pencil */}
    <rect x="44" y="20" width="4" height="24" fill="#FFD700" />
    <rect x="44" y="44" width="4" height="4" fill="#DEB887" />
    <rect x="45" y="48" width="2" height="4" fill="#2F2F2F" />
    <rect x="44" y="16" width="4" height="4" fill="#FF69B4" />
    {/* Written text simulation */}
    <rect x="14" y="18" width="24" height="2" fill="#333" />
    <rect x="14" y="26" width="20" height="2" fill="#333" />
    <rect x="14" y="34" width="28" height="2" fill="#333" />
    <rect x="14" y="42" width="16" height="2" fill="#333" />
  </svg>
);

const PixelLaundry = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    {/* T-shirt */}
    <rect x="16" y="12" width="32" height="4" fill="#4169E1" />
    <rect x="12" y="16" width="8" height="12" fill="#4169E1" />
    <rect x="44" y="16" width="8" height="12" fill="#4169E1" />
    <rect x="16" y="16" width="32" height="36" fill="#4169E1" />
    {/* Collar */}
    <rect x="26" y="12" width="12" height="8" fill="#1a0a2e" />
    {/* Fold lines */}
    <rect x="20" y="28" width="24" height="2" fill="#3158C7" />
    <rect x="20" y="40" width="24" height="2" fill="#3158C7" />
    {/* Sparkles */}
    <rect x="8" y="8" width="2" height="2" fill="#FFD700" />
    <rect x="6" y="10" width="2" height="2" fill="#FFD700" />
    <rect x="10" y="10" width="2" height="2" fill="#FFD700" />
    <rect x="54" y="20" width="2" height="2" fill="#FFD700" />
    <rect x="52" y="22" width="2" height="2" fill="#FFD700" />
    <rect x="56" y="22" width="2" height="2" fill="#FFD700" />
  </svg>
);

const PixelHousehold = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    {/* Plate */}
    <ellipse cx="32" cy="44" rx="20" ry="8" fill="#E8E8E8" />
    <ellipse cx="32" cy="42" rx="16" ry="6" fill="#F5F5F5" />
    {/* Cup */}
    <rect x="8" y="24" width="12" height="16" fill="#00CED1" rx="2" />
    <rect x="18" y="28" width="4" height="8" fill="#00CED1" />
    {/* Sponge */}
    <rect x="44" y="28" width="12" height="8" fill="#98FB98" />
    <rect x="44" y="28" width="12" height="3" fill="#90EE90" />
    {/* Bubbles */}
    {[[26, 20], [30, 16], [36, 18], [42, 14], [24, 12]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="#87CEEB" opacity="0.7" />
    ))}
    {/* Sparkles */}
    <rect x="50" y="8" width="2" height="2" fill="#FFD700" />
    <rect x="48" y="10" width="2" height="2" fill="#FFD700" />
    <rect x="52" y="10" width="2" height="2" fill="#FFD700" />
  </svg>
);

const PixelTools = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full">
    {/* Hammer */}
    <rect x="28" y="8" width="8" height="20" fill="#8B4513" />
    <rect x="22" y="4" width="20" height="8" fill="#808080" />
    <rect x="22" y="4" width="20" height="3" fill="#A9A9A9" />
    {/* Wrench */}
    <rect x="12" y="32" width="4" height="24" fill="#696969" />
    <rect x="8" y="28" width="12" height="8" fill="#808080" />
    <rect x="10" y="30" width="4" height="4" fill="#1a0a2e" />
    {/* Screwdriver */}
    <rect x="44" y="24" width="4" height="28" fill="#FFD700" />
    <rect x="44" y="52" width="4" height="4" fill="#FF6347" />
    <rect x="45" y="56" width="2" height="6" fill="#808080" />
    {/* Sparks */}
    <rect x="36" y="16" width="2" height="2" fill="#FFD700" />
    <rect x="40" y="12" width="2" height="2" fill="#FF6347" />
    <rect x="38" y="20" width="2" height="2" fill="#FFA500" />
  </svg>
);

// Video preview component with retro CRT effect
const RetroVideoPreview = ({ quest }) => {
  const PixelArt = {
    handwritten: PixelHandwriting,
    laundry: PixelLaundry,
    homeops: PixelHousehold,
    tools: PixelTools,
  }[quest.id];

  return (
    <motion.div
      className="relative w-full aspect-video bg-arcade-black rounded-lg overflow-hidden border-4 border-dark-border"
      whileHover={{ scale: 1.02 }}
    >
      {/* CRT scanline effect */}
      <div className="absolute inset-0 pointer-events-none z-10 opacity-20"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
        }}
      />

      {/* Pixel art content */}
      <div className="absolute inset-0 flex items-center justify-center p-4 bg-gradient-to-br from-deep-purple to-arcade-black">
        <motion.div
          className="w-24 h-24"
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {PixelArt && <PixelArt />}
        </motion.div>
      </div>

      {/* Play button overlay */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity"
        whileHover={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      >
        <motion.div
          className={`w-14 h-14 bg-${quest.color} rounded-full flex items-center justify-center`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Play className="w-6 h-6 text-dark-border ml-1" fill="currentColor" />
        </motion.div>
      </motion.div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-neon-pink" />
      <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-neon-pink" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-neon-pink" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-neon-pink" />

      {/* Media type badge */}
      <div className={`absolute top-2 right-2 px-2 py-1 rounded text-[8px] font-pixel ${
        quest.mediaType === 'VIDEO OR IMAGES'
          ? 'bg-neon-pink/80 text-white'
          : 'bg-lavender/80 text-dark-border'
      }`}>
        {quest.mediaType === 'VIDEO OR IMAGES' ? 'VID/IMG' : 'VIDEO'}
      </div>
    </motion.div>
  );
};

const SideQuestsSection = () => {
  const quests = [
    {
      id: 'handwritten',
      icon: PenTool,
      title: 'HANDWRITTEN NOTES & PROBLEM SOLVING',
      audience: 'Public high schools & colleges',
      audienceIcon: BookOpen,
      color: 'neon-pink',
      borderColor: 'border-neon-pink',
      bgGlow: 'shadow-glow-pink',
      whatToRecord: [
        'Handwritten solutions to math/science problems',
        'Step-by-step reasoning visible on paper',
        'Diagrams, graphs, and visual explanations',
        'Show writing process, not just final answer',
      ],
      whatGoodLooksLike: 'Clear handwriting, steady camera angle from above, good lighting on paper, complete problem-solving process shown.',
      mediaType: 'VIDEO OR IMAGES',
    },
    {
      id: 'laundry',
      icon: Shirt,
      title: 'FOLDING CLOTHES',
      audience: 'Laundry company staff + household laundry helpers',
      audienceIcon: Users,
      color: 'trophy-gold',
      borderColor: 'border-trophy-gold',
      bgGlow: 'shadow-glow-gold',
      whatToRecord: [
        'Folding techniques for different garments',
        'Sorting clothes by type/color/fabric',
        'Handling delicate items properly',
      ],
      whatGoodLooksLike: 'Hands and garments clearly visible, consistent folding technique, organized workspace, full fold shown from start to finish.',
      mediaType: 'VIDEO ONLY',
    },
    {
      id: 'homeops',
      icon: Home,
      title: 'HOUSEHOLD TASKS',
      audience: 'Household helpers / Yaya groups',
      audienceIcon: Users,
      color: 'success-green',
      borderColor: 'border-success-green',
      bgGlow: 'shadow-glow-green',
      whatToRecord: [
        'Unloading and organizing groceries',
        'Washing dishes efficiently',
        'Cleaning kitchen surfaces',
        'Opening/closing household objects',
        'Object transfers and basic tool usage',
      ],
      whatGoodLooksLike: 'Clear view of hands and objects, natural household environment, complete task cycles, safe and efficient movements.',
      mediaType: 'VIDEO ONLY',
    },
    {
      id: 'tools',
      icon: Wrench,
      title: 'TOOL USAGE',
      audience: 'Construction / Manual workers',
      audienceIcon: HardHat,
      color: 'warning-orange',
      borderColor: 'border-warning-orange',
      bgGlow: '',
      whatToRecord: [
        'Proper tool handling and grip',
        'Repetitive actions (hammering, screwing, etc.)',
        'Safety-conscious movements',
        'Tool setup and storage',
      ],
      whatGoodLooksLike: 'Tools and hands clearly visible, safety gear if applicable, repetitive actions with consistent form, proper technique demonstrated.',
      mediaType: 'VIDEO ONLY',
    },
  ];

  return (
    <section id="side-quests" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(255,0,128,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,0,128,0.3) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div
            className="inline-flex items-center gap-3 mb-4"
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-6 h-6 text-trophy-gold" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-foreground">
              SIDE QUESTS
            </h2>
            <Star className="w-6 h-6 text-trophy-gold" />
          </motion.div>
          <p className="font-terminal text-xl text-lavender">
            Choose your quest and start earning
          </p>
          <motion.p
            className="font-terminal text-sm text-muted mt-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Hover over the preview to see sample content
          </motion.p>
        </motion.div>

        {/* Quest Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {quests.map((quest, index) => (
            <motion.div
              key={quest.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              whileHover={{ y: -5 }}
              className={`quest-card arcade-panel rounded-lg overflow-hidden border-l-4 ${quest.borderColor} relative group`}
            >
              {/* Glow effect on hover */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${quest.bgGlow}`} />

              {/* Card Header */}
              <div className={`p-5 bg-${quest.color}/10 border-b-2 border-dark-border relative`}>
                <div className="flex items-start gap-4">
                  <motion.div
                    className={`w-14 h-14 bg-${quest.color} rounded flex items-center justify-center border-2 border-dark-border flex-shrink-0`}
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <quest.icon className="w-7 h-7 text-dark-border" />
                  </motion.div>
                  <div className="flex-1">
                    <h3 className="font-pixel text-[10px] sm:text-xs text-foreground mb-2">
                      {quest.title}
                    </h3>
                    <div className="flex items-center gap-2 text-lavender">
                      <quest.audienceIcon className="w-4 h-4" />
                      <span className="font-terminal text-base">{quest.audience}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 space-y-5 relative">
                {/* Retro Video Preview */}
                <RetroVideoPreview quest={quest} />

                {/* What to Record */}
                <div>
                  <h4 className="font-pixel text-[10px] text-neon-pink mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    WHAT TO RECORD
                  </h4>
                  <ul className="space-y-2">
                    {quest.whatToRecord.map((item, idx) => (
                      <motion.li
                        key={idx}
                        className="flex items-start gap-2"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * idx }}
                      >
                        <span className="text-trophy-gold mt-1">▸</span>
                        <span className="font-terminal text-lg text-foreground">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* What Good Looks Like */}
                <div className="bg-deep-purple/50 p-4 rounded border border-panel-purple">
                  <h4 className="font-pixel text-[10px] text-success-green mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    WHAT GOOD LOOKS LIKE
                  </h4>
                  <p className="font-terminal text-base text-lavender">
                    {quest.whatGoodLooksLike}
                  </p>
                </div>

                {/* Leaderboard Badge */}
                <div className="flex items-center justify-between pt-2 border-t border-panel-purple">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-trophy-gold" />
                      <span className="font-pixel text-[10px] text-trophy-gold">EARNS REWARDS</span>
                    </div>
                    <span className={`font-pixel text-[8px] px-2 py-1 rounded ${quest.mediaType === 'VIDEO OR IMAGES' ? 'bg-neon-pink/20 text-neon-pink border border-neon-pink/50' : 'bg-lavender/20 text-lavender border border-lavender/50'}`}>
                      {quest.mediaType}
                    </span>
                  </div>
                  <motion.a
                    href="#submit"
                    className={`btn-arcade font-pixel text-[8px] px-3 py-2 bg-${quest.color} text-dark-border hover:opacity-90`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    START QUEST
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SideQuestsSection;
